import React from 'react'
import { Box, Typography, Alert, Tooltip, IconButton } from '@mui/material'
import { Info as InfoIcon } from '@mui/icons-material'
import { AiGenerationUsageInfo } from '@/features/internal/drugs/types/soapTemplate'

interface AiSoapTemplateGenerationUsageDisplayProps {
  usageInfo: AiGenerationUsageInfo
}

/**
 * AI SOAPテンプレート生成機能の利用状況表示コンポーネント
 */
const AiSoapTemplateGenerationUsageDisplay: React.FC<
  AiSoapTemplateGenerationUsageDisplayProps
> = ({ usageInfo }) => {
  const isOverLimit = usageInfo.remaining <= 0
  const isNearLimit = usageInfo.remaining <= 5 && !isOverLimit

  const usagePercentage = (usageInfo.daily_used / usageInfo.daily_limit) * 100

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          本日の利用状況
        </Typography>
        <Tooltip
          title={`AIテンプレート生成は1日${usageInfo.daily_limit}回まで利用可能です`}
        >
          <IconButton size="small">
            <InfoIcon fontSize="small" color="action" />
          </IconButton>
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* プログレスバー */}
        <Box sx={{ flex: 1, position: 'relative', mt: 3, mb: 2 }}>
          <Box
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={[
                {
                  height: '100%',
                  width: `${usagePercentage}%`,
                  transition: 'none',
                },
                isNearLimit
                  ? {
                      backgroundColor: 'warning.main',
                    }
                  : {
                      backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    },
              ]}
            />
          </Box>

          {/* 現在の使用量ピン */}
          <Box
            sx={{
              position: 'absolute',
              left: `${usagePercentage}%`,
              top: -8,
              transform: 'translateX(-50%)',
              zIndex: 2,
            }}
          >
            <Box
              sx={{
                backgroundColor: '#333',
                color: 'white',
                fontSize: '10px',
                padding: '2px 4px',
                borderRadius: '2px',
                position: 'absolute',
                bottom: '4px',
                left: '50%',
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
              }}
            >
              {usageInfo.daily_used}回使用
            </Box>
            <Box
              sx={{
                width: 0,
                height: 0,
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
                borderTop: '6px solid #333',
              }}
            />
          </Box>

          {/* 目盛りライン */}
          {[25, 50, 75].map((percentage) => (
            <Box
              key={percentage}
              sx={{
                position: 'absolute',
                left: `${percentage}%`,
                top: 8,
                width: '1px',
                height: '4px',
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
              }}
            />
          ))}

          {/* 目盛り数字 */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 1,
              fontSize: '10px',
              color: 'text.secondary',
            }}
          >
            <span>0</span>
            <span>{Math.floor(usageInfo.daily_limit * 0.25)}</span>
            <span>{Math.floor(usageInfo.daily_limit * 0.5)}</span>
            <span>{Math.floor(usageInfo.daily_limit * 0.75)}</span>
            <span>{usageInfo.daily_limit}</span>
          </Box>
        </Box>

        {/* 残り回数表示 */}
        <Box sx={{ textAlign: 'right', minWidth: 'fit-content' }}>
          <Typography
            variant="body2"
            color={
              isOverLimit
                ? 'error'
                : isNearLimit
                ? 'warning.main'
                : 'text.primary'
            }
          >
            残り{usageInfo.remaining}回
          </Typography>
          <Typography variant="caption" color="text.secondary">
            (上限{usageInfo.daily_limit}回)
          </Typography>
        </Box>
      </Box>
      {/* アラート表示 */}
      {isOverLimit && (
        <Alert severity="error" sx={{ mt: 2 }}>
          本日の利用上限に達しました。明日の0時以降に再度お試しください。
        </Alert>
      )}
      {isNearLimit && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          残り利用回数が少なくなっています。
        </Alert>
      )}
    </Box>
  )
}

export default AiSoapTemplateGenerationUsageDisplay
